import { useCallback, useMemo, useState } from 'react';
import { toHighlightedMarkId } from './markNavigation';

export type MarkFocus = {
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  /** The active row's id while focus is anywhere in the chart, else null. */
  highlightedId: string | null;
  /** Spread onto the element containing both the canvas and the mark list. */
  containerFocusProps: {
    onFocus: React.FocusEventHandler<HTMLElement>;
    onBlur: React.FocusEventHandler<HTMLElement>;
  };
};

const isLeavingContainer = (event: React.FocusEvent<HTMLElement>): boolean =>
  !(
    event.relatedTarget instanceof Node &&
    event.currentTarget.contains(event.relatedTarget)
  );

/** The active row of a chart's mark list, and whether the canvas should highlight it. */
export const useMarkFocus = (ids: readonly string[]): MarkFocus => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const idSet = useMemo(() => new Set(ids), [ids]);

  const onFocus = useCallback(() => setIsFocusWithin(true), []);
  const onBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
    if (isLeavingContainer(event)) setIsFocusWithin(false);
  }, []);
  const containerFocusProps = useMemo(
    () => ({ onFocus, onBlur }),
    [onFocus, onBlur]
  );

  return {
    activeId,
    setActiveId,
    highlightedId: toHighlightedMarkId({ activeId, ids: idSet, isFocusWithin }),
    containerFocusProps,
  };
};
